"use client";

import { createContext } from "react";

/**
 * Debug bounding-box overlay for text objects (text-rendering & typography LLD
 * §6.6 "bounded to the card by default"). When enabled — editor surfaces only —
 * a text object draws an outline of its wrap box:
 *   • blue  = box fully inside the video
 *   • amber = box inside the video BUT its content overflows the box
 *   • red   = the box itself crosses the video edge
 * Off by default, so `/view` and the Lambda MP4 never show it.
 */
/**
 * Which editor surface is drawing, if any. Decides WHICH objects get an outline
 * and in what colour — see the outline table in
 * RichTextCardObject.render.test.tsx.
 *
 * "off" is the default so `/view` and the Lambda render stay clean by omission:
 * a surface that forgets to opt in draws nothing, rather than leaking debug
 * chrome onto a recipient's card.
 */
export type EditorSurface = "artist" | "consumer" | "off";

export const BoundsDebugContext = createContext<EditorSurface>("off");

/**
 * Stop-light green, for text a CONSUMER is allowed to edit, shown to the ARTIST.
 *
 * Deliberately not one of BOUNDS_COLORS: on the artist surface it answers a
 * different question ("who owns this text") from those ("does it fit"), so it
 * replaces the bounds colour on those objects instead of competing with it.
 * Chosen to stay legible on the #e7e5e0 stage and to be distinguishable from
 * the in-bounds blue at a glance.
 */
export const EDITABLE_OUTLINE_COLOR = "#16a34a";

export type BoundsStatus = "in" | "content-overflow" | "out";

export const BOUNDS_COLORS: Record<BoundsStatus, string> = {
  in: "#2563eb", // blue
  "content-overflow": "#d97706", // amber
  out: "#dc2626", // red
};

export type BoundsInput = {
  /** outer translate (composition px) — the object's anchor/center */
  anchorX: number;
  anchorY: number;
  /** rendered box natural size, pre-scale (composition px): offsetWidth/Height */
  boxW: number;
  boxH: number;
  /** content natural size (composition px): scrollWidth/Height */
  contentW: number;
  contentH: number;
  /** transform.scale as a percent (100 = no magnification) */
  scale: number;
  /** transform.rotate in degrees (0 = axis-aligned). Optional. */
  rotate?: number;
  /** the card's intrinsic dimensions (resolutionX/Y) */
  resolutionX: number;
  resolutionY: number;
};

// Sub-pixel slack so exact-fit boxes aren't flagged by rounding.
const EPS = 0.5;

/**
 * Classify a text object's wrap box against the video bounds.
 *
 * GEOMETRY — this has to mirror `useCurrentFrameStyle` exactly, or the outline
 * is drawn in one place and classified in another. That style sets
 * `transform: translate(-50%,-50%)` with `transform-origin: top left`, plus the
 * individual `rotate` and `scale` properties. CSS composes those as
 * `rotate × scale × transform`, so the -50% translate is applied FIRST and puts
 * the box's own centre on the origin; `rotate` and `scale` then both pivot
 * about that same point. **The box is therefore centred on the anchor and both
 * scaled and rotated about its centre** — it does not grow from its top-left
 * corner. (It did in the first implementation, which put a scale-500 box a full
 * box-width to the right of where it is actually drawn.)
 *
 * Precedence: an out-of-video box is the most severe, so it wins over content
 * overflow. Pass the CURRENT-FRAME transform, not the resting one, so the
 * classification matches the geometry drawn at this frame.
 */
export function boundsStatus({
  anchorX,
  anchorY,
  boxW,
  boxH,
  contentW,
  contentH,
  scale,
  rotate = 0,
  resolutionX,
  resolutionY,
}: BoundsInput): BoundsStatus {
  const s = scale / 100;
  // Centre-pivoted: half the SCALED extent either side of the anchor.
  const halfW = (boxW * s) / 2;
  const halfH = (boxH * s) / 2;
  const left0 = anchorX - halfW;
  const right0 = anchorX + halfW;
  const top0 = anchorY - halfH;
  const bottom0 = anchorY + halfH;

  let left = left0;
  let top = top0;
  let right = right0;
  let bottom = bottom0;

  if (rotate % 360 !== 0) {
    // Rotate the four corners about the box centre; the AABB is their extent.
    const cx = (left0 + right0) / 2;
    const cy = (top0 + bottom0) / 2;
    const rad = (rotate * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    // Typed as fixed-length tuples so indexing stays `number` under
    // noUncheckedIndexedAccess (a plain number[][] widens to number|undefined).
    const corners: [number, number][] = [
      [left0, top0],
      [right0, top0],
      [right0, bottom0],
      [left0, bottom0],
    ];
    const rotated = corners.map(([x, y]): [number, number] => {
      const dx = x - cx;
      const dy = y - cy;
      return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
    });
    const xs = rotated.map((c) => c[0]);
    const ys = rotated.map((c) => c[1]);
    left = Math.min(...xs);
    right = Math.max(...xs);
    top = Math.min(...ys);
    bottom = Math.max(...ys);
  }

  const boxOut =
    left < -EPS ||
    top < -EPS ||
    right > resolutionX + EPS ||
    bottom > resolutionY + EPS;
  if (boxOut) return "out";

  const contentOverflow = contentW > boxW + EPS || contentH > boxH + EPS;
  return contentOverflow ? "content-overflow" : "in";
}
